import React, { useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BatteryFullIcon,
  BatteryHalfIcon,
  BatteryIcon,
  SearchIcon,
  NoWifi,
  WifiIcon,
  BatteryWarningIcon,
  QuestionMarkIcon,
} from "@components/icons";
import { GREEN } from "@helper/colors";

const WorkingSensorIcon = ({ title }) => (
  <span
    title={title}
    style={{
      width: "18px",
      height: "18px",
      borderRadius: "999px",
      background: GREEN,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.08)",
      flex: "0 0 auto",
    }}
  >
    <WifiIcon color="#ffffff" width="11px" />
  </span>
);

function filterProducts({ filterTerm, sensors }) {
  if (!filterTerm) {
    return sensors;
  }
  const term = filterTerm.toLowerCase();
  return sensors.filter(
    (product) =>
      (product.name && product.name.toLowerCase().includes(term)) ||
      (product.hardware_serial && product.hardware_serial.toLowerCase().includes(term)) ||
      (product.imei && product.imei.toLowerCase().includes(term))
  );
}

function SensorMobileListView({ sensors }) {
  const [filterTerm, setFilterTerm] = useState("");
  const { t } = useTranslation();

  const filteredProducts = filterProducts({ filterTerm, sensors });

  function updateFilterHandler(event) {
    setFilterTerm(event.target.value);
  }

  const getSensorStateLabel = (state) =>
    ({
      0: t("working"),
      1: t("battery_empty"),
      2: t("maintenance_needed"),
      3: t("not_sending"),
      4: t("unknown"),
      5: t("defect"),
    }[state] || t("unknown"));

  const getSensorStateVisual = (sensor) => {
    const isSendingRaw = localStorage.getItem("isSending");
    let isSending = {};
    try {
      isSending = isSendingRaw ? JSON.parse(isSendingRaw) : {};
    } catch (error) {
      isSending = {};
    }
    const isSendingSensor = Boolean(isSending[sensor.hardware_serial]);

    switch (sensor.sensor_state) {
      case 0:
        return <WorkingSensorIcon title={t("working")} />;
      case 1:
        return <BatteryWarningIcon color="#CD3100" width="15px" title={t("battery_empty")} />;
      case 2:
        return (
          <span title={t("maintenance_needed")} style={{ fontSize: "15px", lineHeight: 1 }}>
            🔧
          </span>
        );
      case 3:
        return isSendingSensor ? (
          <WorkingSensorIcon title={t("working")} />
        ) : (
          <NoWifi color="rgb(194 193 188)" width="15px" title={t("not_sending")} />
        );
      case 4:
        return <QuestionMarkIcon color="rgb(194 193 188)" width="15px" title={t("unknown")} />;
      case 5:
        return (
          <span title={t("defect")} style={{ fontSize: "15px", lineHeight: 1, color: "#CD3100" }}>
            ✖
          </span>
        );
      default:
        return <QuestionMarkIcon color="rgb(194 193 188)" width="15px" title={t("unknown")} />;
    }
  };

  return (
    <>
      <SearchForm>
        <input
          type="text"
          onChange={updateFilterHandler}
          className="form-control search-box"
          placeholder={t("search_sensor")}
          style={{
            padding: "12px 1em 12px 2.5em",
            color: "var(--color-text-primary, #0e1d2c)",
            backgroundColor: "var(--color-bg-surface, #ffffff)",
            borderColor: "var(--color-border-default, #cdd7df)",
          }}
        />
        <SearchIcon top={0} width="30px" left="5px" />
      </SearchForm>
      <div role="mobileList">
        {filteredProducts?.map((sensor) => (
          <MobileSensorLink
            to={`/sensors?selectedSensor=${sensor.id}`}
            key={sensor.id}
            className="list-unstyled py-2 d-flex justify-content-between clickable"
          >
            <div style={{ width: "40%", minWidth: 0 }}>
              <h6 className="mb-1" style={{ color: "inherit" }}>
                <b>{sensor.name}</b>
              </h6>
              <SecondaryText>
                {sensor.network_type === "NB" ? sensor.imei : sensor.hardware_serial}
              </SecondaryText>
            </div>
            <div
              className="d-flex align-items-center gap-1"
              style={{ minWidth: "110px", justifyContent: "flex-start" }}
            >
              {getSensorStateVisual(sensor)}
              <SecondarySmall style={{ lineHeight: 1.1 }}>
                {getSensorStateLabel(sensor.sensor_state)}
              </SecondarySmall>
            </div>
            <div
              style={{
                minWidth: "72px",
                textAlign: "right",
                color: "var(--color-text-primary, #0e1d2c)",
              }}
            >
              <small>
                {sensor.battery == null ? (
                  <BatteryHalfIcon size={"1.3rem"} color="#888888" />
                ) : sensor.battery > 60 ? (
                  <BatteryFullIcon size={"1.3rem"} color="#19b7bf" />
                ) : sensor.battery < 30 ? (
                  <BatteryIcon size={"1.3rem"} color="#CD3100" />
                ) : (
                  <BatteryHalfIcon size={"1.3rem"} color="#FEC400" />
                )}
                &nbsp; {sensor.battery != null ? `${sensor.battery}%` : "?"}
              </small>
            </div>
          </MobileSensorLink>
        ))}
      </div>
    </>
  );
}

export default SensorMobileListView;

const SearchForm = styled.div`
  position: relative;
`;

const MobileSensorLink = styled(Link)`
  color: var(--color-text-primary, #0e1d2c);
  text-decoration: none;

  &:visited,
  &:active,
  &:focus {
    color: var(--color-text-primary, #0e1d2c);
    text-decoration: none;
  }
`;

const SecondaryText = styled.i`
  color: var(--color-text-secondary, #3d5a6b);
`;

const SecondarySmall = styled.small`
  color: var(--color-text-secondary, #3d5a6b);
`;
